import React from 'react';
import { Link } from 'react-router-dom';
import { variantLabel } from '../../Parts/bomsApi';
import type { BuildCapacityData, DashboardProduct } from '../types';
import { Progress } from '../../components/ui/Progress';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import { buttonClass } from '../../components/ui/Button';

const SHOWN = 5;

const ModelBars: React.FC<{ model: DashboardProduct }> = ({ model }) => {
  const lines = model.lines.slice(0, SHOWN);
  const more = model.lines.length - lines.length;
  const max = Math.max(...lines.map((l) => l.builds), 0);
  // The lines that actually limit a build, per variant: a pyro-only line at 0
  // does not limit the light-show build.
  const limitingIds = new Set(model.capacity.map((c) => c.limited_by?.line_id).filter((id) => id != null));

  return (
    <div>
      <div className="mb-2 flex items-baseline gap-2">
        <span className="font-bold text-on-surface">{model.name}</span>
        {(model.model_code ?? model.sku) !== model.name && (
          <span className="text-xs text-on-surface-variant">{model.model_code ?? model.sku}</span>
        )}
      </div>
      {lines.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No parts list yet</p>
      ) : (
        <ul className="space-y-1.5">
          {lines.map((l) => {
            const isLimit = limitingIds.has(l.id);
            // Every part tied at zero limits nothing: no part is the bottleneck.
            const bottleneck = isLimit && max > l.builds;
            return (
              <li key={l.id} className="flex items-center gap-3">
                <div className="flex w-44 min-w-0 shrink-0 items-center gap-1.5">
                  <span
                    className={`truncate text-xs ${
                      bottleneck ? 'font-semibold text-on-surface' : 'text-on-surface-variant'
                    }`}
                    title={l.target_name}
                  >
                    {l.target_name}
                  </span>
                  {l.variant && (
                    <span className="shrink-0 rounded-full bg-surface-container-highest px-1.5 py-0.5 text-[10px] text-on-surface-variant">
                      {variantLabel(l.variant)}
                    </span>
                  )}
                </div>
                <Progress
                  className="flex-1"
                  value={l.builds}
                  max={max}
                  label={`${l.target_name} builds`}
                  size="md"
                  tone={isLimit ? 'primary' : 'neutral'}
                  glow={bottleneck}
                  fillIn
                />
                <div
                  className={`w-12 shrink-0 text-right text-sm tabular-nums ${
                    isLimit ? 'font-bold text-on-surface' : 'text-on-surface-variant'
                  }`}
                >
                  {l.builds}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {more > 0 && (
        <p className="mt-2 text-xs text-on-surface-variant">
          {more} more {more === 1 ? 'line' : 'lines'}
        </p>
      )}
    </div>
  );
};

const BuildCapacityPartsModule: React.FC<{ buildCapacity?: Partial<BuildCapacityData> }> = ({
  buildCapacity,
}) => {
  const products = buildCapacity?.products ?? [];

  return (
    <Panel title="Capacity by part">
      {products.length === 0 ? (
        <EmptyState
          variant="area"
          title="No parts lists on the dashboard yet"
          action={<Link to="/parts/boms" className={buttonClass('tonal', 'sm')}>Open Product BOMs</Link>}
        />
      ) : (
        <div className="space-y-5">
          {products.map((m) => (
            <ModelBars key={m.id} model={m} />
          ))}
        </div>
      )}
    </Panel>
  );
};

export default BuildCapacityPartsModule;
